import React, { useState, useEffect } from "react"
import "./Header.css"
import { Link } from "react-router-dom"
import technumnewlogo from "../../assets/Images/Technumopus/headerLogo.webp"
import hamburgerIcon from "../../assets/Images/Technumopus/Header_li_align-right.svg"

const Header = () => {
  const [openDropdown, setOpenDropdown] = useState(null)
  const [dropdownTimeout, setDropdownTimeout] = useState(null)
  const [isScrolled, setIsScrolled] = useState(false)
  const [activeItem, setActiveItem] = useState(null)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [mobileDropdowns, setMobileDropdowns] = useState({}) // track sidebar dropdowns

  const handleMouseEnter = (dropdown) => {
    if (dropdownTimeout) {
      clearTimeout(dropdownTimeout)
      setDropdownTimeout(null)
    }
    setOpenDropdown(dropdown)
  }

  const handleMouseLeave = () => {
    const timeout = setTimeout(() => {
      setOpenDropdown(null)
    }, 150)
    setDropdownTimeout(timeout)
  }

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY
      const heroSection = document.querySelector(".tohome-hero-wrap")
      const heroHeight = heroSection ? heroSection.offsetHeight : window.innerHeight
      const shouldBeScrolled = scrollY > heroHeight - 100

      if (shouldBeScrolled !== isScrolled) {
        setIsScrolled(shouldBeScrolled)
      }
    }

    handleScroll()

    window.addEventListener("scroll", handleScroll, { passive: true })
    window.addEventListener("resize", handleScroll, { passive: true })

    return () => {
      window.removeEventListener("scroll", handleScroll)
      window.removeEventListener("resize", handleScroll)
      if (dropdownTimeout) {
        clearTimeout(dropdownTimeout)
      }
    }
  }, [isScrolled, dropdownTimeout])

  // A11y: close the mobile menu on Escape, and lock background scroll while open.
  // The sidebar slides over content; without scroll lock, iOS Safari lets the
  // page scroll behind the overlay.
  useEffect(() => {
    if (!isMobileMenuOpen) return undefined
    const handleKey = (e) => {
      if (e.key === "Escape") setIsMobileMenuOpen(false)
    }
    document.addEventListener("keydown", handleKey)
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      document.removeEventListener("keydown", handleKey)
      document.body.style.overflow = previousOverflow
    }
  }, [isMobileMenuOpen])

  const handleItemClick = (item, e) => {
    if (item === "services" || item === "industries") {
      e.preventDefault()
    }
    setActiveItem(item)
    setIsMobileMenuOpen(false) // close menu after click on mobile
  }

  const handleDropdownItemClick = (parentItem) => {
    setActiveItem(parentItem)
    setOpenDropdown(null) // close the hover dropdown as soon as an item is picked
    setIsMobileMenuOpen(false) // close menu after click on mobile
  }

  // toggle nested dropdown inside sidebar
  const toggleMobileDropdown = (menu) => {
    setMobileDropdowns((prev) => ({
      ...prev,
      [menu]: !prev[menu],
    }))
  }

  return (
    <>
    <header
      className={`technumopus-header ${
        isScrolled ? "technumopus-header--scrolled" : ""
      }`}
    >
      <nav className="technumopus-nav-container">
        {/* Logo left */}
        <Link to="" className="technumopus-logo">
          <div className="technumopus-logo-wrapper">
            <img
              src={technumnewlogo}
              alt="Technum Opus"
              width="1281"
              height="258"
              fetchpriority="high"
              decoding="async"
            />
          </div>
        </Link>

        {/* Hamburger right (visible <768px) */}
        <button
          className="technumopus-hamburger"
          aria-label="Toggle menu"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          <img src={hamburgerIcon} alt="Menu" decoding="async" />
        </button>

        {/* Desktop nav */}
        <div
          className={`technumopus-nav-content ${
            isMobileMenuOpen ? "technumopus-nav-content--open" : ""
          }`}
        >
          <ul className="technumopus-nav-menu">
            <li className="technumopus-nav-item">
              <Link
                to="/about-us"
                onClick={() => handleItemClick("about")}
                className={activeItem === "about" ? "active" : ""}
              >
                About
              </Link>
            </li>

            {/* AWS Partner — Top-level highlighted */}
            <li className="technumopus-nav-item technumopus-nav-aws">
              <Link
                to="/aws-partner"
                onClick={() => handleItemClick("aws")}
                className={activeItem === "aws" ? "active" : ""}
              >
                <span className="technumopus-aws-badge">AWS Partner</span>
              </Link>
            </li>

            {/* Services Dropdown */}
            <li
              className="technumopus-nav-item"
              onMouseEnter={() => handleMouseEnter("services")}
              onMouseLeave={handleMouseLeave}
            >
              <Link
                to="/services"
                onClick={(e) => handleItemClick("services", e)}
                className={activeItem === "services" ? "active" : ""}
                aria-haspopup="true"
                aria-expanded={openDropdown === "services"}
              >
                Services
              </Link>
              {openDropdown === "services" && (
                <div className="technumopus-dropdown" role="menu">
                  <Link
                    to="/digital-transformation"
                    onClick={() => handleDropdownItemClick("services")}
                  >
                    Digital Transformation
                  </Link>
                  <Link
                    to="/computer-vision"
                    onClick={() => handleDropdownItemClick("services")}
                  >
                    Computer Vision Intelligence
                  </Link>
                  <Link
                    to="/industry-4-0"
                    onClick={() => handleDropdownItemClick("services")}
                  >
                    Industry 4.0 Implementation
                  </Link>
                  <Link
                    to="/cloud-migration"
                    onClick={() => handleDropdownItemClick("services")}
                  >
                    Cloud Consultancy & Migration
                  </Link>
                  {/* <Link
                    to="/automation"
                    onClick={() => handleDropdownItemClick("services")}
                  >
                    Automation Solutions
                  </Link> */}
                  <Link
                    to="/technology-infrastructure"
                    onClick={() => handleDropdownItemClick("services")}
                  >
                    Technology Infrastructure Solutions
                  </Link>
                </div>
              )}
            </li>

            {/* Industries Dropdown */}
            <li
              className="technumopus-nav-item"
              onMouseEnter={() => handleMouseEnter("industries")}
              onMouseLeave={handleMouseLeave}
            >
              <Link
                to="/industries"
                onClick={(e) => handleItemClick("industries", e)}
                className={activeItem === "industries" ? "active" : ""}
                aria-haspopup="true"
                aria-expanded={openDropdown === "industries"}
              >
                Industries
              </Link>
              {openDropdown === "industries" && (
                <div
                  className="technumopus-dropdown technumopus-dropdown-industries"
                  role="menu"
                >
                  <div className="technumopus-dropdown-group">
                    <h5>Our Expertise</h5>
                    <Link
                      to="/textiles-apparel"
                      onClick={() => handleDropdownItemClick("industries")}
                      className="technumopus-featured"
                    >
                      ★ Textiles & Apparel (Proven Excellence)
                    </Link>
                  </div>
                  <div className="technumopus-dropdown-group">
                    <Link
                      to="/precision-manufacturing"
                      onClick={() => handleDropdownItemClick("industries")}
                    >
                      Precision Manufacturing
                    </Link>
                  </div>
                  <div className="technumopus-dropdown-group">
                    <Link
                      to="/process-industries"
                      onClick={() => handleDropdownItemClick("industries")}
                    >
                      Process Industries
                    </Link>
                  </div>
                  <div className="technumopus-dropdown-group">
                    <Link
                      to="/materials-finishing"
                      onClick={() => handleDropdownItemClick("industries")}
                    >
                      Materials & Finishing
                    </Link>
                  </div>
                </div>
              )}
            </li>

            <li className="technumopus-nav-item">
              <Link
                to="/onified-ai"
                onClick={() => handleItemClick("onified")}
                className={activeItem === "onified" ? "active" : ""}
              >
                Onified.ai
              </Link>
            </li>
            <li className="technumopus-nav-item">
              <Link
                to="/case-studies"
                onClick={() => handleItemClick("case-studies")}
                className={activeItem === "case-studies" ? "active" : ""}
              >
                Case Studies
              </Link>
            </li>
            <li className="technumopus-nav-item">
              <Link
                to="/blog"
                onClick={() => handleItemClick("blog")}
                className={activeItem === "blog" ? "active" : ""}
              >
                Blog
              </Link>
            </li>
            <li className="technumopus-nav-item">
              <Link
                to="/careers"
                onClick={() => handleItemClick("careers")}
                className={activeItem === "careers" ? "active" : ""}
              >
                Careers
              </Link>
            </li>
            <li className="technumopus-nav-item">
              <Link
                to="/contact"
                onClick={() => handleItemClick("contact")}
                className={activeItem === "contact" ? "active" : ""}
              >
                Contact
              </Link>
            </li>
          </ul>

          {/* Show desktop button only when sidebar is closed */}
          {!isMobileMenuOpen && (
            <Link to="/contact">
              <button
                className="technumopus-btn-primary"
                aria-label="Book Consultation"
              >
                Talk to an Expert
              </button>
            </Link>
          )}
        </div>
      </nav>
    </header>

    {/* Sidebar backdrop overlay — outside header to avoid backdrop-filter containment */}
    <div
      className={`technumopus-sidebar-overlay ${
        isMobileMenuOpen ? "technumopus-sidebar-overlay--visible" : ""
      }`}
      onClick={() => setIsMobileMenuOpen(false)}
    />

    {/* Sidebar (mobile menu) — outside header to avoid backdrop-filter containment */}
    <div
      className={`technumopus-sidebar ${
        isMobileMenuOpen ? "technumopus-sidebar--open" : ""
      }`}
    >
      {/* Top Row */}
      <div className="technumopus-sidebar-top">
        <Link to="" className="technumopus-logo" onClick={() => setIsMobileMenuOpen(false)}>
          <div className="technumopus-logo-wrapper">
            <img
              src={technumnewlogo}
              alt="Technum Opus"
              width="1281"
              height="258"
              decoding="async"
            />
          </div>
        </Link>
        <button
          className="technumopus-sidebar-close"
          aria-label="Close menu"
          onClick={() => setIsMobileMenuOpen(false)}
        >
          ✕
        </button>
      </div>

      {/* Sidebar Nav */}
      <ul className="technumopus-sidebar-menu">
        <li>
          <Link to="/about-us" onClick={() => handleItemClick("about")}>
            About
          </Link>
        </li>
        <li className="technumopus-sidebar-aws">
          <Link to="/aws-partner" onClick={() => handleItemClick("aws")}>
            <span className="technumopus-aws-badge">AWS Partner</span>
          </Link>
        </li>
        <li>
          <button
            className="technumopus-sidebar-dropdown-toggle"
            onClick={() => toggleMobileDropdown("services")}
          >
            Services <span>{mobileDropdowns.services ? "−" : "+"}</span>
          </button>
          {mobileDropdowns.services && (
            <ul className="technumopus-sidebar-submenu">
              <li>
                <Link to="/digital-transformation" onClick={() => handleDropdownItemClick("services")}>
                  Digital Transformation
                </Link>
              </li>
              <li>
                <Link to="/computer-vision" onClick={() => handleDropdownItemClick("services")}>
                  Computer Vision Intelligence
                </Link>
              </li>
              <li>
                <Link to="/industry-4-0" onClick={() => handleDropdownItemClick("services")}>
                  Industry 4.0 Implementation
                </Link>
              </li>
              <li>
                <Link to="/cloud-migration" onClick={() => handleDropdownItemClick("services")}>
                  Cloud Consultancy & Migration
                </Link>
              </li>
              <li>
                <Link to="/automation" onClick={() => handleDropdownItemClick("services")}>
                  Automation Solutions
                </Link>
              </li>
              <li>
                <Link to="/technology-infrastructure" onClick={() => handleDropdownItemClick("services")}>
                  Technology Infrastructure Solutions
                </Link>
              </li>
            </ul>
          )}
        </li>
        <li>
          <button
            className="technumopus-sidebar-dropdown-toggle"
            onClick={() => toggleMobileDropdown("industries")}
          >
            Industries <span>{mobileDropdowns.industries ? "−" : "+"}</span>
          </button>
          {mobileDropdowns.industries && (
            <ul className="technumopus-sidebar-submenu">
              <li>
                <Link to="/textiles-apparel" onClick={() => handleDropdownItemClick("industries")}>
                  ★ Textiles & Apparel
                </Link>
              </li>
              <li>
                <Link to="/precision-manufacturing" onClick={() => handleDropdownItemClick("industries")}>
                  Precision Manufacturing
                </Link>
              </li>
              <li>
                <Link to="/process-industries" onClick={() => handleDropdownItemClick("industries")}>
                  Process Industries
                </Link>
              </li>
              <li>
                <Link to="/materials-finishing" onClick={() => handleDropdownItemClick("industries")}>
                  Materials & Finishing
                </Link>
              </li>
            </ul>
          )}
        </li>
        <li>
          <Link to="/onified-ai" onClick={() => handleItemClick("onified")}>
            Onified.ai
          </Link>
        </li>
        <li>
          <Link to="/case-studies" onClick={() => handleItemClick("case-studies")}>
            Case Studies
          </Link>
        </li>
        <li>
          <Link to="/blog" onClick={() => handleItemClick("blog")}>
            Blog
          </Link>
        </li>
        <li>
          <Link to="/careers" onClick={() => handleItemClick("careers")}>
            Careers
          </Link>
        </li>
        <li>
          <Link to="/contact" onClick={() => handleItemClick("contact")}>
            Contact
          </Link>
        </li>
      </ul>

      {/* CTA Button at bottom */}
      <div className="technumopus-sidebar-cta">
        <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)}>
          <button
            className="technumopus-btn-primary"
            aria-label="Book Consultation"
          >
            Talk to an Expert
          </button>
        </Link>
      </div>
    </div>
    </>
  )
}

export default Header
